import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Heart, HelpingHand, ShieldCheck } from 'lucide-react';

const FreemasonryPage = () => {
  return (
    <div className="p-6 md:p-12 lg:p-16" data-testid="freemasonry-page">
      <div className="max-w-4xl">
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6">
          Freemasonry
        </h1>

        {/* Introduction and Core Tenets */}
        <section className="mb-10" data-testid="freemasonry-intro">
          <div className="flex flex-col lg:flex-row gap-8 mb-10">
            <div className="flex-1">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Freemasonry originated in Scotland from the system of lodges used by stonemasons when
                travelling to the building-sites of churches and the great houses. From these humble
                beginnings, it has grown into one of the world's largest fraternities with thousands
                of lodges across Scotland and throughout the world.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Freemasonry today seeks to benefit all of humankind through the inculcation of high moral
                and spiritual values, using the stonemason's working tools as allegorical guides in a series
                of ritual dramas. These values have three basic tenets:
              </p>
            </div>
            <div className="lg:w-80 flex-shrink-0">
              <Card className="overflow-hidden border-gray-100 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-0">
                  <img
                    src="/images/ashlar.jpg"
                    alt="The Rough and Perfect Ashlar"
                    className="w-full h-64 object-cover"
                    data-testid="ashlar-image"
                  />
                  <div className="bg-white p-4">
                    <p className="text-sm font-medium text-gray-600 text-center">The Rough and Perfect Ashlar</p>
                    <p className="text-xs text-gray-400 text-center mt-1">Representing the moral and spiritual transition</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group" data-testid="tenet-brotherly-love">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center mb-4 transition-colors group-hover:bg-emerald-100">
                  <Heart className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-emerald-700 mb-3">Brotherly Love</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Tolerance and respect towards fellow human beings, fostering friendship and understanding
                  across all boundaries.
                </p>
              </CardContent>
            </Card>

            <Card className="border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group" data-testid="tenet-relief">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center mb-4 transition-colors group-hover:bg-emerald-100">
                  <HelpingHand className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-emerald-700 mb-3">Relief</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Charity and generosity to anyone less fortunate, supporting those in need through both
                  direct assistance and charitable works.
                </p>
              </CardContent>
            </Card>

            <Card className="border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group" data-testid="tenet-truth">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center mb-4 transition-colors group-hover:bg-emerald-100">
                  <ShieldCheck className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-emerald-700 mb-3">Truth</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Being true to oneself, others, and high moral standards, living with integrity and honour
                  in all aspects of life.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Inclusivity */}
        <section data-testid="inclusivity-section">
          <Card className="bg-gradient-to-br from-gray-50 to-white border-gray-100">
            <CardContent className="p-6 md:p-8">
              <p className="text-lg text-gray-700 leading-relaxed">
                True to these principles, Freemasonry is non-religious, non-political, and welcomes members
                from all walks of life irrespective of race, religion, sexuality, or socio-economic status.
                St David (University Lodge) No. 36 is particularly diverse, reflecting as it does the
                cosmopolitan nature of the Scottish further and higher education system.
              </p>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default FreemasonryPage;
